if (!customElements.get('review-cards')) {
  class ReviewCards extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      // Remove clones left over from an earlier connection.
      this.track.querySelectorAll('[data-clone]').forEach((el) => el.remove());
      this.originals = [...this.track.querySelectorAll('[data-card]')];
      this.loops = this.originals.length > 1;

      this.onPrev = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
      this.onScroll = () => this.scheduleWrap();
      this.onResize = () => this.scheduleWrap();
      this.onBlockSelect = (event) => {
        const card = event.target.closest('[data-card]');
        if (card && this.originals.includes(card)) this.centerCard(card, false);
      };

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.loops) this.buildClones();

      this.setupAutoplay();

      // Start with the middle card centred, as in the design.
      const middle = this.originals[Math.floor((this.originals.length - 1) / 2)];
      if (middle) this.centerCard(middle, false);
    }

    disconnectedCallback() {
      clearTimeout(this.wrapTimer);
      clearTimeout(this.resumeTimer);
      cancelAnimationFrame(this.frame);
      this.visibility?.disconnect();
      this.mobileQuery?.removeEventListener('change', this.onModeChange);
      document.removeEventListener('visibilitychange', this.onModeChange);
      ['pointerdown', 'touchstart', 'wheel', 'focusin', 'mouseenter'].forEach((name) =>
        this.track?.removeEventListener(name, this.onInteract),
      );
      ['pointerup', 'touchend', 'touchcancel', 'mouseleave', 'focusout'].forEach((name) =>
        this.track?.removeEventListener(name, this.onRelease),
      );
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // Slow, continuous movement. It pauses while the visitor touches or hovers
    // the row and carries on a moment after they let go.
    setupAutoplay() {
      this.mode = this.dataset.autoplay || 'off';
      this.speed = parseFloat(this.dataset.speed) || 30;
      this.interacting = false;
      this.inView = true;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.onInteract = () => {
        this.interacting = true;
        clearTimeout(this.resumeTimer);
      };
      this.onRelease = () => {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => {
          this.interacting = false;
          this.position = this.track.scrollLeft;
        }, 1800);
      };
      this.onModeChange = () => this.updateAutoplay();

      ['pointerdown', 'touchstart', 'wheel', 'focusin', 'mouseenter'].forEach((name) =>
        this.track.addEventListener(name, this.onInteract, { passive: true }),
      );
      ['pointerup', 'touchend', 'touchcancel', 'mouseleave', 'focusout'].forEach((name) =>
        this.track.addEventListener(name, this.onRelease, { passive: true }),
      );
      this.mobileQuery.addEventListener('change', this.onModeChange);
      document.addEventListener('visibilitychange', this.onModeChange);

      this.visibility = new IntersectionObserver((entries) => {
        this.inView = entries.some((entry) => entry.isIntersecting);
        this.updateAutoplay();
      });
      this.visibility.observe(this);

      this.updateAutoplay();
    }

    updateAutoplay() {
      const allowed =
        this.loops &&
        !this.reduceMotion &&
        !document.hidden &&
        this.inView &&
        (this.mode === 'all' || (this.mode === 'mobile' && this.mobileQuery.matches));

      if (allowed && !this.playing) {
        this.playing = true;
        this.track.style.scrollSnapType = 'none';
        this.position = this.track.scrollLeft;
        this.lastTime = 0;
        this.frame = requestAnimationFrame((time) => this.tick(time));
      } else if (!allowed && this.playing) {
        this.playing = false;
        cancelAnimationFrame(this.frame);
        this.track.style.scrollSnapType = '';
      }
    }

    tick(time) {
      if (!this.playing) return;
      const elapsed = this.lastTime ? Math.min(time - this.lastTime, 64) : 0;
      this.lastTime = time;

      if (this.interacting) {
        this.position = this.track.scrollLeft;
      } else {
        this.position += (this.speed * elapsed) / 1000;
        this.track.scrollLeft = this.position;
        this.frameCount = (this.frameCount || 0) + 1;
        if (this.frameCount % 20 === 0) {
          const before = this.track.scrollLeft;
          this.wrap(true);
          this.position += this.track.scrollLeft - before;
        }
      }
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    get reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Copies of the cards before and after the originals, so the row never ends.
    buildClones() {
      const first = this.originals[0];
      const last = this.originals[this.originals.length - 1];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const setWidth =
        last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
      const sets = Math.max(
        1,
        Math.ceil(this.track.clientWidth / Math.max(setWidth, 1)),
      );

      const makeSet = () =>
        this.originals.map((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('data-clone', '');
          clone.removeAttribute('data-card');
          clone.removeAttribute('data-shopify-editor-block');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          return clone;
        });

      for (let i = 0; i < sets; i += 1) {
        const before = makeSet();
        const after = makeSet();
        before.reverse().forEach((el) => first.parentNode.insertBefore(el, this.track.firstChild));
        after.forEach((el) => this.track.appendChild(el));
      }
    }

    centerCard(card, smooth = true) {
      const left =
        card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
      this.track.scrollTo({
        left,
        behavior: smooth && !this.reduceMotion ? 'smooth' : 'auto',
      });
    }

    scrollByCard(direction) {
      const card = this.originals[0];
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: direction * (card.offsetWidth + gap),
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    // Once scrolling settles, jump by exactly one set of cards. The picture
    // doesn't change, but the row is back in the middle.
    scheduleWrap() {
      if (!this.loops) return;
      clearTimeout(this.wrapTimer);
      this.wrapTimer = setTimeout(() => this.wrap(), 120);
    }

    wrap(keepSnapOff = false) {
      const first = this.originals[0];
      const last = this.originals[this.originals.length - 1];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const pitch = first.offsetWidth + gap;
      const setWidth = last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
      const start = first.offsetLeft - (this.track.clientWidth - first.offsetWidth) / 2;

      let position = this.track.scrollLeft;
      let moved = 0;
      while (position + moved < start - pitch / 2) moved += setWidth;
      while (position + moved > start + setWidth - pitch / 2) moved -= setWidth;
      if (!moved) return;

      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = position + moved;
      if (keepSnapOff || this.playing) return;
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
      });
    }
  }

  customElements.define('review-cards', ReviewCards);
}
